import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Cart Icon Component
 *
 * Updates the cart icon bubble whenever the cart changes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = [
    'cartBubble',
    'cartBubbleText',
    'cartBubbleCount',
  ];

  /**
   * Get current cart item count.
   *
   * @returns {number}
   */
  get currentCartCount() {
    return parseInt(
      this.refs.cartBubbleCount.textContent ?? '0',
      10
    );
  }

  /**
   * Set current cart item count.
   *
   * @param {number} value
   */
  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent =
      value < 100 ? String(value) : '';
  }

  /**
   * Component connected.
   */
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.addEventListener(
      'pageshow',
      this.onPageShow
    );

    this.ensureCartBubbleIsCorrect();
  }

  /**
   * Component disconnected.
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.removeEventListener(
      'pageshow',
      this.onPageShow
    );
  }

  /**
   * Handles the page show event.
   *
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles cart update events.
   *
   * @param {CartLinesUpdateEvent} event
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount =
          cart?.totalQuantity ??
          detail?.itemCount ??
          0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-icon] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Render cart bubble.
   *
   * @param {number} itemCount
   * @param {boolean} animate
   */
  renderCartBubble = async (
    itemCount,
    animate = true
  ) => {
    const {
      cartBubble,
      cartBubbleText,
      cartBubbleCount,
    } = this.refs;

    // Show/hide cart count
    cartBubbleCount.classList.toggle(
      'hidden',
      itemCount === 0
    );

    cartBubble.classList.toggle(
      'visually-hidden',
      itemCount === 0
    );

    // Update count
    this.currentCartCount = itemCount;

    // Add/remove cart state class
    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    // Save count in session storage
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    // Don't animate when disabled or cart is empty
    if (!animate || itemCount === 0) {
      return;
    }

    // Wait for browser to render the updated state
    await new Promise((resolve) =>
      requestAnimationFrame(resolve)
    );

    // Start animation
    cartBubble.classList.add(
      'cart-bubble--animating'
    );

    // Wait until animation finishes
    await onAnimationEnd(cartBubbleText);

    // Remove animation class
    cartBubble.classList.remove(
      'cart-bubble--animating'
    );
  };

  /**
   * Ensure cart bubble count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) {
      return;
    }

    const sessionStorageCount =
      sessionStorage.getItem('cart-count');

    // Nothing to check
    if (sessionStorageCount === null) {
      return;
    }

    const visibleCount =
      this.refs.cartBubbleCount.textContent;

    try {
      const {
        value,
        timestamp,
      } = JSON.parse(sessionStorageCount);

      // Count is already correct
      if (value === visibleCount) {
        return;
      }

      // Only use recently stored value
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(
            count,
            false
          );
        }
      }
    } catch (error) {
      // Ignore invalid session storage data
    }
  };
}

/**
 * Register custom element.
 */
if (!customElements.get('cart-icon')) {
  customElements.define(
    'cart-icon',
    CartIcon
  );
}